// resolveAssignment.js — start-year resolution for the two parcel assignment tables.
//
// ⛔ START-YEAR SEMANTICS. A row records a CHANGE, effective from its value_year and standing until
// superseded. The row for a given year is the LATEST row whose value_year <= the requested year — NOT an
// exact match. An inline `.eq('value_year', y)` against these tables returns NOTHING for every year
// between changes, which looks like "no consultant" rather than "no change that year". That exact bug is
// already logged against expected_bills/collectors; do not reintroduce it here. Always come through this
// file.
//
// Cross-year isolation (2026-08-16_year_isolation_*) means writes are single-year and never touch another
// year, so resolution can trust that a year's rows are exactly what was entered for that year.
import { supabase } from '../supabase';

// The FIRM engaged on a parcel for a value year. Returns { row, year } where year is the year the
// assignment actually started (may be earlier than asked), or { row: null } if nothing is on file at or
// before the requested year. A row with consultant_id null is a TOMBSTONE — the engagement ended that
// year — and is returned as such, not treated as "no assignment": the caller must show "none" rather
// than falling further back to a superseded firm.
export async function resolveFirmAssignment(parcelId, valueYear) {
  if (!parcelId || valueYear == null || valueYear === '') return { row: null, year: null };
  const { data, error } = await supabase
    .from('consultant_assignments')
    .select('*, consultant:consultant_id (id, company)')
    .eq('parcel_id', parcelId)
    .lte('value_year', Number(valueYear))
    .order('value_year', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return { row: data || null, year: data ? data.value_year : null };
}

// BULK sibling of resolveFirmAssignment — THE SAME RULE (the latest row with value_year <= the requested
// year), resolved for many parcels at once. Lives beside the single-parcel version deliberately: they are one
// rule, and a report that resolved assignments differently from the parcel screen would disagree with it on
// exactly the rows a user is most likely to check.
//
// ⛔ RESOLUTION, NOT EXACT MATCH. Every current assignment is backfilled at 2021, so `.eq('value_year', 2026)`
// returns nothing — indistinguishable from "no consultant assigned", which is the empty column this replaces.
// A 2021 assignment MUST show on a 2026 view.
//
// ⛔ SET-BASED, not a per-row lookup. The single-parcel version costs one query; at 3,795 parcels that shape
// would cost 3,795. Batched .in() (100 ids — URL length) with a .range() loop inside each batch, because
// consultant_assignments will pass 1000 rows and PostgREST caps a response there SILENTLY.
//
// The winner is chosen by MAX(value_year) rather than by relying on row order, so pagination across pages
// cannot pick a stale row: order is a hint here, the reduce is the guarantee.
// Returns Map<parcel_id, { value_year, company }>.
export async function resolveFirmAssignmentsBulk(parcelIds, valueYear) {
  const out = new Map();
  if (!parcelIds || !parcelIds.length || valueYear == null || valueYear === '') return out;
  const yr = Number(valueYear);
  if (Number.isNaN(yr)) return out;
  for (let i = 0; i < parcelIds.length; i += 100) {
    const batch = parcelIds.slice(i, i + 100);
    for (let from = 0; ; from += 1000) {
      const { data, error } = await supabase
        .from('consultant_assignments')
        .select('parcel_id, value_year, consultant:consultant_id (company)')
        .in('parcel_id', batch)
        .lte('value_year', yr)
        .order('value_year', { ascending: false })
        .range(from, from + 999);
      if (error) throw error;
      (data || []).forEach(r => {
        const prev = out.get(r.parcel_id);
        if (!prev || Number(r.value_year) > Number(prev.value_year)) {
          out.set(r.parcel_id, { value_year: r.value_year, company: r.consultant?.company || null });
        }
      });
      if (!data || data.length < 1000) break;
    }
  }
  return out;
}

// A consultant firm's Primary contact — the person Key Contacts shows for the parcel's firm when the parcel has no
// consultant people of its own. ConsultantContactModal keeps Primary to one person per firm. → { first_name,
// last_name, email } | null (the firm has no Primary contact on file).
export async function firmPrimaryContact(consultantId) {
  if (!consultantId) return null;
  const { data, error } = await supabase
    .from('consultant_contact_roles')
    .select('contact:contact_id (first_name, last_name, email)')
    .eq('consultant_id', consultantId)
    .eq('role', 'primary')
    .limit(1);
  if (error) throw error;
  return data?.[0]?.contact || null;
}

// Every firm assignment row for a parcel, newest first — the year → firm timeline the modal shows so a
// user can see they are APPENDING a change, not overwriting a field.
export async function listFirmAssignments(parcelId) {
  if (!parcelId) return [];
  const { data, error } = await supabase
    .from('consultant_assignments')
    .select('*, consultant:consultant_id (id, company)')
    .eq('parcel_id', parcelId)
    .order('value_year', { ascending: false });
  if (error) throw error;
  return data || [];
}

// The EXPERT-role roster for a parcel at a value year, resolved as a SET.
//
// ⛔ SET RESOLUTION, PER ROLE: for each role independently, find max(value_year) <= requested, then take
// ALL rows at that year. A year's rows REPLACE the previous year's roster for that role — a generation,
// not a diff. Resolving per-row instead would mix a 2021 holder with a 2027 one and silently invent a
// roster that was never entered.
//
// Returns { [role]: { year, people: [{ id, first_name, last_name, email, pool, contactId }] } }.
export async function resolveRoleRoster(parcelId, valueYear) {
  if (!parcelId || valueYear == null || valueYear === '') return {};
  const { data, error } = await supabase
    .from('parcel_contact_roles')
    .select('*, client_contact:client_contact_id (id, first_name, last_name, email), consultant_contact:consultant_contact_id (id, first_name, last_name, email)')
    .eq('parcel_id', parcelId)
    .lte('value_year', Number(valueYear))
    .order('value_year', { ascending: false });
  if (error) throw error;

  const out = {};
  (data || []).forEach(r => {
    const cur = out[r.role];
    // Rows arrive newest-first, so the first year seen for a role IS its resolved generation; later
    // (older) years are superseded and dropped.
    if (cur && cur.year !== r.value_year) return;
    const c = r.client_contact || r.consultant_contact;
    if (!c) return;   // FK cascade removed the person; the row is debris, not a holder
    const person = {
      id: r.id, contactId: c.id, pool: r.client_contact_id ? 'client' : 'consultant',
      first_name: c.first_name, last_name: c.last_name, email: c.email,
    };
    if (!cur) out[r.role] = { year: r.value_year, people: [person] };
    else cur.people.push(person);
  });
  return out;
}
